// Web variant of `mapCamera.ts`: swaps the react-native-fs file for
// localStorage (like `cache.web.ts` does for fetched content). The exported API
// is identical so the shared `useMapCenterCache` hook works on both platforms.
import type { Coordinates } from './geolocation';

const STORAGE_KEY = 'map-center';

type WebStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

const getStorage = (): WebStorage | null => {
  try {
    const storage = (globalThis as { localStorage?: WebStorage }).localStorage;

    if (!storage) {
      return null;
    }

    // Make sure localStorage is actually usable (sandboxed/private contexts).
    const probe = '__keyfiyat_map_center_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);

    return storage;
  } catch {
    return null;
  }
};

const toCoordinates = (value: unknown): Coordinates | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const { latitude, longitude } = value as Partial<Coordinates>;

  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return null;
  }

  return { latitude, longitude };
};

export const readCachedMapCenter = async (): Promise<Coordinates | null> => {
  try {
    const stored = getStorage()?.getItem(STORAGE_KEY);

    if (!stored) {
      return null;
    }

    return toCoordinates(JSON.parse(stored));
  } catch (caughtError) {
    console.warn('Failed to read the cached map center', caughtError);
    return null;
  }
};

export const writeCachedMapCenter = async (
  center: Coordinates,
): Promise<void> => {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(center));
  } catch (caughtError) {
    console.warn('Failed to cache the map center', caughtError);
  }
};
